import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { AchievementId, AwardedEvent, ShareEligibleEvent, ShareTarget } from '@tg/shared';
import { useSocketEvent } from '@/socket/socket';
import { useLocale } from '@/i18n/useLocale';
import { useSession } from '@/stores/session';
import { selectInGame, useScreen } from '@/stores/screen';
import { useResolvedTheme } from '@/theme/theme';
import { Button, cx } from '@/ui';
import { sound } from '@/fx';
import { shareApi } from '@/features/share/api';
import { ShareSheet } from '@/features/share/ShareSheet';
import { badgeUri, momentUri } from './art';
import { achName, awardTitle, gameName } from './text';
import s from './achievements.module.css';
import { UiIcon } from '@/art/UiIcon';

/** a bar stays this long unless the player acts */
export const AWARD_BAR_MS = 8000;
/** the "share this win" bar waits until the win celebration is over (spec §4.1) */
export const ELIGIBLE_DELAY_MS = 2500;

type Item = { key: string; kind: 'award'; e: AwardedEvent } | { key: string; kind: 'win'; e: ShareEligibleEvent };

/**
 * Quiet bar at the bottom (above the phone tab bar): "Achievement · <name>" / "×128.5 in Koi Pond" with Share and Close.
 * One at a time (queue), 8 s each, no coins, no animation under reduced motion. A new achievement chimes
 * (`common/achievement`, only with sound on); a big-win moment is silent — its game already celebrated. Not the Toaster — that one
 * has no buttons. Game screens are not touched: there the bar moves to the top, under the game header, and is more
 * compact on a phone — never over the bet panel and the Spin button (honest play: nothing gets in the way of a round).
 */
export function AchievementListener() {
  const intl = useIntl();
  const locale = useLocale();
  const theme = useResolvedTheme();
  const reduced = useSession((st) => st.reducedMotion || st.osReducedMotion);
  const [queue, setQueue] = useState<Item[]>([]);
  const [sharing, setSharing] = useState<Item | null>(null);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const inGame = useScreen(selectInGame);

  const push = useCallback((it: Item) => setQueue((q) => (q.some((x) => x.key === it.key) ? q : [...q, it])), []);
  const drop = useCallback((key: string) => setQueue((q) => q.filter((x) => x.key !== key)), []);

  useSocketEvent<[AwardedEvent]>('achievement:awarded', (e) => push({ key: `a:${e.awardId}`, kind: 'award', e }));
  useSocketEvent<[ShareEligibleEvent]>('share:eligible', (e) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      push({ key: `w:${e.betId}`, kind: 'win', e });
    }, ELIGIBLE_DELAY_MS);
    timers.current.add(t);
  });
  useEffect(() => {
    const set = timers.current;
    return () => set.forEach(clearTimeout);
  }, []);

  const current = queue[0] ?? null;
  const chimeKey = current?.kind === 'award' && current.e.kind === 'achievement' ? current.key : null;
  useEffect(() => {
    if (chimeKey) sound.play('common/achievement');
  }, [chimeKey]);
  // the bar leaves on its own; paused while its share sheet is open
  useEffect(() => {
    if (!current || sharing) return;
    const t = setTimeout(() => drop(current.key), AWARD_BAR_MS);
    return () => clearTimeout(t);
  }, [current, sharing, drop]);

  const load = useCallback((): Promise<ShareTarget> => {
    if (!sharing) return Promise.reject(new Error('nothing to share'));
    return sharing.kind === 'win' ? shareApi.moment(sharing.e.betId, locale) : shareApi.award(sharing.e.awardId, locale);
  }, [sharing, locale]);

  let icon = '';
  let title = '';
  if (current?.kind === 'award') {
    const e = current.e;
    if (e.kind === 'achievement') {
      icon = badgeUri(e.ach as AchievementId, e.medal, theme);
      title = intl.formatMessage({ id: 'achievements.toast.awarded' }, { name: achName(intl, e.ach, e.value) });
    } else {
      icon = momentUri(e.ach === 'place' ? 'place' : 'win', theme);
      title = awardTitle(intl, { kind: e.kind, ach: e.ach, value: e.value, game: e.game });
    }
  } else if (current?.kind === 'win') {
    icon = momentUri('win', theme);
    title = intl.formatMessage({ id: 'achievements.toast.moment' }, { x: current.e.x / 100, game: gameName(intl, current.e.game) });
  }

  return (
    <>
      {current && (
        <div className={cx(s.toastHost, inGame && s.toastHostGame)} data-in-game={inGame || undefined}>
          <div key={current.key} className={cx(s.toast, !reduced && s.toastAnim)} role="status" aria-live="polite">
            <img className={s.toastIcon} src={icon} alt="" aria-hidden="true" width={48} height={48} />
            <span className={s.toastText}>
              <span className={s.toastTitle}>{title}</span>
            </span>
            <span className={s.toastActions}>
              <Button size="sm" minWidth={88} onClick={() => setSharing(current)}>
                <FormattedMessage id="achievements.share" />
              </Button>
              <button
                type="button"
                className={s.toastClose}
                aria-label={intl.formatMessage({ id: 'achievements.toast.dismiss' })}
                onClick={() => drop(current.key)}
              >
                <UiIcon name="close" size={16} fallback="×" />
              </button>
            </span>
          </div>
        </div>
      )}
      <ShareSheet
        open={sharing !== null}
        onClose={() => {
          if (sharing) drop(sharing.key);
          setSharing(null);
        }}
        load={load}
      />
    </>
  );
}
